import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useCallback } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import { getFeatureFlag } from 'app-components/flux/feature-flags/featureFlagsSelectors';
import { RuleProvider } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import {
  getEnabledOverrides,
  getInitialRule,
  getNormalizedPayeeNames,
} from 'components/Dialogs/RuleDialogs/shared/ruleDialogUtils';
import { submitTransactionRule } from 'components/Dialogs/RuleDialogs/shared/ruleDialogSubmit';
import RuleContainer from 'containers/Settings/ChildPages/RulesSimplifi/RuleContainer';

export type TransactionRuleDialogProps = {
  transaction?: TransactionProps;
  rule?: TransactionRuleType;
  hasAIEdits?: boolean;
  handleClose?: (rule?: TransactionRuleType) => void;
  isDrawerOverlay?: boolean;
  originalIsReviewed?: boolean | null;
};

const TransactionRulesDialogComponent = ({
  transaction,
  rule,
  handleClose,
  isDrawerOverlay,
  originalIsReviewed,
}: TransactionRuleDialogProps) => {
  const appDispatch = useAppDispatch();

  const txnRuleV2 = Boolean(useAppSelector((state) => getFeatureFlag(state, 'qbpUsageTxnrulesv2'))); // ensure re-renders on state changes
  const businessCoaIds = useAppSelector(categoriesSelectors.getBusinessCoaIdsSet);

  const handleOnUpdate = useCallback(
    (newRule?: TransactionRuleType) => {
      if (newRule) {
        submitTransactionRule({ dispatch: appDispatch, rule: newRule });
      }
      handleClose?.(newRule);
    },
    [appDispatch, handleClose],
  );

  const { statementPayeeName, inferredPayeeName } = getNormalizedPayeeNames(transaction);

  const enabledFlags = getEnabledOverrides({ transaction, originalIsReviewed });

  // When creating a rule from a transaction, pre-fill the rule with the transaction's data
  // When editing an existing rule, use that rule's data
  // Otherwise, start with a clear state
  const getRule = useCallback((): TransactionRuleType => {
    return getInitialRule({
      transaction,
      rule,
      businessCoaIds,
      statementPayeeName,
      txnRuleV2,
    });
  }, [transaction, rule, businessCoaIds, statementPayeeName, txnRuleV2]);

  return (
    <RuleProvider
      rule={getRule()}
      onUpdate={handleOnUpdate}
      statementPayeeName={statementPayeeName ?? undefined}
      inferredPayeeName={inferredPayeeName ?? undefined}
      fromTxn={!!transaction}
      enabledOverrides={transaction ? enabledFlags : {}}
    >
      <RuleContainer isDrawerOverlay={isDrawerOverlay} />
    </RuleProvider>
  );
};

const TransactionRulesDialog = React.memo(TransactionRulesDialogComponent);

TransactionRulesDialog.displayName = 'TransactionRulesDialog';

export default TransactionRulesDialog;
